import { useEffect } from "preact/hooks";

export default function Gallery({ container }) {
  useEffect(() => {
    if (!container) return;

    const track = container.querySelector("[data-gallery-track]");
    const slides = Array.from(container.querySelectorAll("[data-gallery-slide]"));
    const thumbs = Array.from(container.querySelectorAll("[data-gallery-thumb]"));
    const prevBtn = container.querySelector("[data-gallery-prev]");
    const nextBtn = container.querySelector("[data-gallery-next]");

    if (!track || slides.length === 0) return;

    let currentIndex = 0;

    // Update which thumbnail is active and update aria-hidden on slides
    const setActiveIndex = (index) => {
      currentIndex = index;

      slides.forEach((slide, i) => {
        slide.setAttribute("aria-hidden", i === index ? "false" : "true");
      });

      thumbs.forEach((thumb) => {
        const thumbIndex = parseInt(thumb.dataset.index, 10);
        if (thumbIndex === index) {
          thumb.dataset.active = "";
          thumb.setAttribute("aria-current", "true");
        } else {
          delete thumb.dataset.active;
          thumb.removeAttribute("aria-current");
        }
      });

      if (prevBtn) prevBtn.disabled = index === 0;
      if (nextBtn) nextBtn.disabled = index === slides.length - 1;
    };

    // Scroll track to target slide index
    let scrollingProgrammatically = false;
    let scrollEndTimer = null;
    const clearScrolling = () => {
      scrollingProgrammatically = false;
      clearTimeout(scrollEndTimer);
    };
    track.addEventListener("scrollend", clearScrolling);
    const goTo = (index) => {
      const clamped = Math.max(0, Math.min(index, slides.length - 1));
      setActiveIndex(clamped);
      scrollingProgrammatically = true;
      clearTimeout(scrollEndTimer);
      track.scrollTo({ left: clamped * track.clientWidth, behavior: "smooth" });
      // Fallback for browsers without scrollend support
      scrollEndTimer = setTimeout(clearScrolling, 1000);
    };

    // IntersectionObserver: detect which slide is most visible during manual swipe/scroll
    const observer = new IntersectionObserver(
      (entries) => {
        if (scrollingProgrammatically) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index, 10);
            setActiveIndex(index);
          }
        });
      },
      { root: track, threshold: 0.5 }
    );

    slides.forEach((slide) => observer.observe(slide));

    // Prev / next buttons (desktop)
    const handlePrev = () => goTo(currentIndex - 1);
    const handleNext = () => goTo(currentIndex + 1);

    if (prevBtn) prevBtn.addEventListener("click", handlePrev);
    if (nextBtn) nextBtn.addEventListener("click", handleNext);

    // Thumbnail clicks
    const thumbHandlers = thumbs.map((thumb) => {
      const handler = () => goTo(parseInt(thumb.dataset.index, 10));
      thumb.addEventListener("click", handler);
      return handler;
    });

    // Keyboard navigation (left/right arrows) when gallery is focused
    container.setAttribute("tabindex", "0");
    const handleKeydown = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goTo(currentIndex - 1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goTo(currentIndex + 1);
      }
    };
    container.addEventListener("keydown", handleKeydown);

    // Initialise button states
    setActiveIndex(0);

    return () => {
      observer.disconnect();
      clearTimeout(scrollEndTimer);
      track.removeEventListener("scrollend", clearScrolling);
      if (prevBtn) prevBtn.removeEventListener("click", handlePrev);
      if (nextBtn) nextBtn.removeEventListener("click", handleNext);
      thumbs.forEach((thumb, i) => thumb.removeEventListener("click", thumbHandlers[i]));
      container.removeEventListener("keydown", handleKeydown);
      container.removeAttribute("tabindex");
    };
  }, []);

  return null;
}
